<template>
  <section class="admin-content" id="vendor-account-page">
    <Navbar />
    <main class="admin-main">
      <div class="page-header">
        <div class="container">
          <div class="d-flex align-items-center text-white" style="padding: 32px 0;">
            <div class="header-icon-wrap mr-3">
              <img v-if="vendor && vendor.logo_url" :src="vendor.logo_url" alt="Business logo" class="header-logo" />
              <i v-else class="fe fe-briefcase" style="font-size: 22px;"></i>
            </div>
            <div>
              <h4 class="mb-0 font-weight-bold">{{ vendor ? vendor.name : "Vendor account" }}</h4>
              <small style="opacity: .8;" v-if="vendor">
                Status: <span class="status-pill" :class="'status-pill--' + vendor.status">{{ vendor.status }}</span>
                <span class="mx-1">·</span>{{ vendor.active_products_count }} listed products
              </small>
            </div>
          </div>
        </div>
      </div>

      <section style="margin-top: -20px;">
        <div class="container pb-5" v-if="vendor">
          <div v-if="vendor.status !== 'approved'" class="alert" :class="vendor.status === 'pending' ? 'alert-warning' : 'alert-danger'">
            <template v-if="vendor.status === 'pending'">Your business is awaiting approval by the marketplace team. Keep building your catalogue in the meantime.</template>
            <template v-else>Your business is {{ vendor.status }}. <span v-if="vendor.status_reason">Reason: {{ vendor.status_reason }}</span></template>
          </div>

          <ul class="nav nav-pills mb-3" role="tablist">
            <li class="nav-item" v-for="t in tabs" :key="t.key">
              <button type="button" class="nav-link" :class="{ active: tab === t.key }" role="tab" :aria-selected="tab === t.key"
                @click="tab = t.key">
                <i :class="'fe ' + t.icon + ' mr-1'"></i>{{ t.label }}
              </button>
            </li>
          </ul>

          <!-- Profile -->
          <div v-if="tab === 'profile'" class="card shadow-sm form-card">
            <form class="card-body px-4 py-4" @submit.prevent="saveProfile">
              <div class="d-flex align-items-center mb-4" style="gap: 16px;">
                <div class="logo-box">
                  <img v-if="vendor.logo_url" :src="vendor.logo_url" alt="Business logo" />
                  <i v-else class="fe fe-image"></i>
                </div>
                <div v-if="isAdmin">
                  <label for="va-logo" class="btn btn-sm btn-outline-primary mb-0">Upload logo</label>
                  <input id="va-logo" type="file" accept="image/jpeg,image/png,image/webp" class="d-none" @change="uploadLogo" />
                  <div class="small text-muted mt-1">Square image, up to 3 MB.</div>
                </div>
              </div>
              <fieldset :disabled="!isAdmin">
                <div class="form-row">
                  <div class="form-group col-md-6">
                    <label for="va-name" class="form-label-sm">Business name</label>
                    <input id="va-name" v-model="profile.name" class="form-control styled-input" required />
                  </div>
                  <div class="form-group col-md-6">
                    <label for="va-rc" class="form-label-sm">CAC / RC number</label>
                    <input id="va-rc" v-model="profile.rc_number" class="form-control styled-input" />
                  </div>
                  <div class="form-group col-md-6">
                    <label for="va-email" class="form-label-sm">Business email</label>
                    <input id="va-email" v-model="profile.email" type="email" class="form-control styled-input" />
                  </div>
                  <div class="form-group col-md-6">
                    <label for="va-phone" class="form-label-sm">Business phone</label>
                    <input v-phone id="va-phone" v-model="profile.phone_number" class="form-control styled-input" />
                  </div>
                  <div class="form-group col-md-8">
                    <label for="va-address" class="form-label-sm">Address</label>
                    <input id="va-address" v-model="profile.address" class="form-control styled-input" />
                  </div>
                  <div class="form-group col-md-4">
                    <label for="va-state" class="form-label-sm">State</label>
                    <input id="va-state" v-model="profile.state" class="form-control styled-input" />
                  </div>
                  <div class="form-group col-md-6">
                    <label for="va-web" class="form-label-sm">Website</label>
                    <input id="va-web" v-model="profile.website" type="url" class="form-control styled-input" placeholder="https://" />
                  </div>
                  <div class="form-group col-12">
                    <label for="va-desc" class="form-label-sm">About the business (shown on your store page)</label>
                    <textarea id="va-desc" v-model="profile.description" rows="4" class="form-control styled-input"></textarea>
                  </div>
                </div>
                <button v-if="isAdmin" type="submit" class="btn btn-submit"><i class="fe fe-save mr-1"></i>Save profile</button>
              </fieldset>
              <p v-if="!isAdmin" class="small text-muted mt-2 mb-0">Only your vendor admin can change the profile.</p>
            </form>
          </div>

          <!-- Staff -->
          <div v-if="tab === 'staff'" class="row">
            <div class="col-lg-7 mb-4">
              <div class="card shadow-sm table-card">
                <div class="table-responsive">
                  <table class="table align-td-middle mb-0">
                    <thead style="background:#f8fafc;">
                      <tr>
                        <th class="table-th">Name</th>
                        <th class="table-th">Contact</th>
                        <th class="table-th">Role</th>
                        <th class="table-th"><span class="sr-only">Actions</span></th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="s in staff" :key="s.id" class="table-row">
                        <td class="px-4 py-3">
                          <div class="font-weight-bold" style="font-size:.88rem;">{{ s.firstname }} {{ s.lastname }}</div>
                          <div class="small text-muted">{{ s.job_title || "—" }}</div>
                        </td>
                        <td class="py-3 small">{{ s.email }}<br />{{ s.phone_number }}</td>
                        <td class="py-3">
                          <span class="status-pill" :class="s.blocked_at ? 'status-pill--suspended' : (s.user_role == 1 ? 'status-pill--approved' : 'status-pill--pending')">
                            {{ s.blocked_at ? "Blocked" : (s.user_role == 1 ? "Admin" : "Staff") }}
                          </span>
                        </td>
                        <td class="py-3 pr-4 text-right text-nowrap" v-if="s.id !== me.id">
                          <button type="button" class="btn btn-sm btn-view mr-1" @click="updateStaff(s, { is_admin: s.user_role != 1 })">
                            {{ s.user_role == 1 ? "Remove admin" : "Make admin" }}
                          </button>
                          <button type="button" class="btn btn-sm" :class="s.blocked_at ? 'btn-outline-success' : 'btn-outline-danger'"
                            @click="updateStaff(s, { blocked: !s.blocked_at })">
                            {{ s.blocked_at ? "Unblock" : "Block" }}
                          </button>
                        </td>
                        <td class="py-3 pr-4 text-right small text-muted" v-else>You</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            <div class="col-lg-5 mb-4">
              <div class="card shadow-sm form-card">
                <div class="form-card-header">
                  <div class="section-icon"><i class="fe fe-user-plus" style="font-size:13px;"></i></div>
                  <h6 class="mb-0 font-weight-bold text-white">Add staff</h6>
                </div>
                <form class="card-body px-4 py-4" @submit.prevent="addStaff">
                  <div class="form-row">
                    <div class="form-group col-md-6">
                      <label for="st-first" class="form-label-sm">First name</label>
                      <input id="st-first" v-model="staffForm.firstname" class="form-control styled-input" required />
                    </div>
                    <div class="form-group col-md-6">
                      <label for="st-last" class="form-label-sm">Last name</label>
                      <input id="st-last" v-model="staffForm.lastname" class="form-control styled-input" required />
                    </div>
                  </div>
                  <div class="form-group">
                    <label for="st-email" class="form-label-sm">Email (used to sign in)</label>
                    <input id="st-email" v-model="staffForm.email" type="email" class="form-control styled-input" required />
                  </div>
                  <div class="form-group">
                    <label for="st-phone" class="form-label-sm">Phone</label>
                    <input v-phone id="st-phone" v-model="staffForm.phone_number" class="form-control styled-input" required />
                  </div>
                  <div class="form-group">
                    <label for="st-job" class="form-label-sm">Job title</label>
                    <input id="st-job" v-model="staffForm.job_title" class="form-control styled-input" placeholder="Sales, Store keeper…" />
                  </div>
                  <div class="form-group">
                    <label for="st-pass" class="form-label-sm">Temporary password (8+ characters)</label>
                    <input id="st-pass" v-model="staffForm.password" type="text" class="form-control styled-input" required autocomplete="off" />
                  </div>
                  <div class="custom-control custom-checkbox mb-3">
                    <input id="st-admin" v-model="staffForm.is_admin" type="checkbox" class="custom-control-input" />
                    <label class="custom-control-label small" for="st-admin">Admin (can manage profile and staff)</label>
                  </div>
                  <button type="submit" class="btn btn-submit btn-block"><i class="fe fe-user-plus mr-1"></i>Create staff account</button>
                </form>
              </div>
            </div>
          </div>

          <!-- Agency marketplaces -->
          <div v-if="tab === 'agencies'" class="row">
            <div class="col-lg-7 mb-4">
              <div class="card shadow-sm table-card">
                <div class="px-4 pt-4 pb-2">
                  <h6 class="font-weight-bold mb-1">Agency marketplaces</h6>
                  <p class="small text-muted mb-0">
                    You are listed in the public marketplace once approved. Agencies can also add you to their own
                    marketplace for their facilities.
                  </p>
                </div>
                <div class="table-responsive">
                  <table class="table align-td-middle mb-0">
                    <thead style="background:#f8fafc;">
                      <tr><th class="table-th">Agency</th><th class="table-th">Status</th><th class="table-th">Note</th></tr>
                    </thead>
                    <tbody>
                      <tr v-for="a in vendor.agencies" :key="a.id" class="table-row">
                        <td class="px-4 py-3">{{ agencyName(a) }}</td>
                        <td class="py-3"><span class="status-pill" :class="'status-pill--' + a.status">{{ a.status }}</span></td>
                        <td class="py-3 small text-muted">{{ a.reason || "—" }}</td>
                      </tr>
                      <tr v-if="!vendor.agencies.length">
                        <td colspan="3" class="text-center py-4 text-muted small">No agency applications yet.</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            <div class="col-lg-5 mb-4" v-if="isAdmin">
              <div class="card shadow-sm form-card">
                <div class="form-card-header">
                  <div class="section-icon"><i class="fe fe-send" style="font-size:13px;"></i></div>
                  <h6 class="mb-0 font-weight-bold text-white">Apply to an agency</h6>
                </div>
                <form class="card-body px-4 py-4" @submit.prevent="applyToAgency">
                  <div class="form-group">
                    <label for="ag-select" class="form-label-sm">Agency</label>
                    <select id="ag-select" v-model="applyAgencyId" class="form-control styled-input" required>
                      <option value="">Select agency…</option>
                      <option v-for="a in agencies" :key="a.agency_id" :value="a.agency_id">{{ a.name }}</option>
                    </select>
                  </div>
                  <button type="submit" class="btn btn-submit btn-block">Send application</button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  </section>
</template>

<script>
import Navbar from "@/views/Navbar.vue";
import AgenciesJson from "@/jsons/agencies.json";

const blankStaff = () => ({ firstname: "", lastname: "", email: "", phone_number: "", job_title: "", password: "", is_admin: false });

export default {
  components: { Navbar },
  data() {
    let me = {};
    try {
      me = JSON.parse(localStorage.getItem("user") || "{}") || {};
    } catch (e) {
      me = {};
    }
    return {
      me,
      vendor: null,
      isAdmin: false,
      tab: this.$route.query.tab || "profile",
      profile: {},
      staff: [],
      staffForm: blankStaff(),
      agencies: AgenciesJson,
      applyAgencyId: "",
    };
  },
  computed: {
    tabs() {
      const tabs = [{ key: "profile", label: "Business profile", icon: "fe-briefcase" }];
      if (this.isAdmin) {
        tabs.push({ key: "staff", label: "Staff", icon: "fe-users" });
      }
      tabs.push({ key: "agencies", label: "Agency marketplaces", icon: "fe-globe" });
      return tabs;
    },
  },
  watch: {
    tab(value) {
      if (value === "staff") this.fetchStaff();
    },
  },
  created() {
    this.fetchVendor();
  },
  methods: {
    notify(message) {
      this.$breadstick.notify(message, { position: "top-right" });
    },
    errorMessage(err) {
      const data = (err.response && err.response.data) || {};
      return data.errors ? Object.values(data.errors).flat().join(" ") : data.message || "Something went wrong";
    },
    fetchVendor() {
      this.axios
        .get("/api/v1/auth/vendor/me")
        .then((res) => {
          this.vendor = res.data.data;
          this.isAdmin = res.data.is_admin;
          const v = this.vendor;
          this.profile = { name: v.name, rc_number: v.rc_number, email: v.email, phone_number: v.phone_number,
            address: v.address, state: v.state, website: v.website, description: v.description };
          if (this.tab === "staff") this.fetchStaff();
        })
        .catch((err) => this.notify(this.errorMessage(err)));
    },
    agencyName(a) {
      if (a.agency) return a.agency.agency_name || a.agency.firstname;
      const known = this.agencies.find((x) => Number(x.agency_id) === Number(a.agency_id));
      return known ? known.name : `Agency #${a.agency_id}`;
    },
    saveProfile() {
      this.axios
        .put("/api/v1/auth/vendor/me", this.profile)
        .then(() => {
          this.notify("Profile saved");
          this.fetchVendor();
        })
        .catch((err) => this.notify(this.errorMessage(err)));
    },
    uploadLogo(event) {
      const file = event.target.files && event.target.files[0];
      if (!file) return;
      const data = new FormData();
      data.append("logo", file);
      this.axios
        .post("/api/v1/auth/vendor/me/logo", data)
        .then(() => this.fetchVendor())
        .catch((err) => this.notify(this.errorMessage(err)))
        .finally(() => (event.target.value = ""));
    },
    fetchStaff() {
      this.axios.get("/api/v1/auth/vendor/staff").then((res) => (this.staff = res.data.data || [])).catch((err) => this.notify(this.errorMessage(err)));
    },
    addStaff() {
      this.axios
        .post("/api/v1/auth/vendor/staff", this.staffForm)
        .then(() => {
          this.notify(`Account created. ${this.staffForm.firstname} can sign in with ${this.staffForm.email}.`);
          this.staffForm = blankStaff();
          this.fetchStaff();
        })
        .catch((err) => this.notify(this.errorMessage(err)));
    },
    updateStaff(s, changes) {
      this.axios
        .put(`/api/v1/auth/vendor/staff/${s.id}`, changes)
        .then(() => this.fetchStaff())
        .catch((err) => this.notify(this.errorMessage(err)));
    },
    applyToAgency() {
      this.axios
        .post("/api/v1/auth/vendor/agencies", { agency_id: this.applyAgencyId })
        .then(() => {
          this.notify("Application sent");
          this.applyAgencyId = "";
          this.fetchVendor();
        })
        .catch((err) => this.notify(this.errorMessage(err)));
    },
  },
};
</script>

<style scoped>
.page-header { background: linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%); }
.header-icon-wrap { width: 48px; height: 48px; background: rgba(255,255,255,0.15); border-radius: 12px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; overflow: hidden; }
.header-logo { width: 100%; height: 100%; object-fit: cover; }
.form-card { border-radius: 14px; overflow: hidden; border: none; }
.form-card-header { display: flex; align-items: center; gap: 12px; padding: 14px 24px; background: linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%); }
.section-icon { width: 28px; height: 28px; background: rgba(255,255,255,.2); border-radius: 8px; display: flex; align-items: center; justify-content: center; color: #fff; flex-shrink: 0; }
.form-label-sm { font-weight: 600; font-size: .8rem; color: #374151; margin-bottom: 4px; display: block; }
.styled-input { border: 1.5px solid #e2e8f0; border-radius: 8px; font-size: .88rem; }
.styled-input:focus { border-color: #2563eb; box-shadow: 0 0 0 3px rgba(37,99,235,.1); outline: none; }
.table-card { border-radius: 12px; border: none; }
.table-th { font-size: .73rem; text-transform: uppercase; letter-spacing: .04em; color: #64748b; font-weight: 600; border-bottom: 2px solid #e2e8f0; padding: 12px 16px; }
.table-row:hover { background: #f8fafc; }
.logo-box { width: 84px; height: 84px; border-radius: 14px; background: #f1f5f9; display: flex; align-items: center; justify-content: center; color: #94a3b8; font-size: 1.6rem; overflow: hidden; }
.logo-box img { width: 100%; height: 100%; object-fit: cover; }
.status-pill { display: inline-block; padding: 2px 10px; border-radius: 20px; font-size: .75rem; font-weight: 600; text-transform: capitalize; background: #e2e8f0; color: #334155; }
.status-pill--approved { background: #d1fae5; color: #065f46; }
.status-pill--pending { background: #fef3c7; color: #92400e; }
.status-pill--rejected, .status-pill--suspended { background: #fee2e2; color: #991b1b; }
.nav-pills .nav-link { border: none; background: #fff; margin-right: 6px; color: #334155; border-radius: 8px; font-size: .88rem; }
.nav-pills .nav-link.active { background: #1e3a5f; color: #fff; }
.btn-submit { background: linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%); border: none; color: #fff; border-radius: 8px; font-weight: 600; padding: 8px 20px; }
.btn-submit:hover { opacity: .9; color: #fff; }
.btn-view { border-radius: 6px; border: 1px solid #e2e8f0; color: #1e3a5f; background: #fff; font-size: .8rem; }
</style>
